import Layout from '@/components/feature/Layout';
import FaqAccordion from '@/components/feature/FaqAccordion';
import { PageBreadcrumb, RelatedLinks, ctaSidebar } from '@/pages/shared/PageHelpers';
import { products } from '@/mocks/products';
import { applications } from '@/mocks/applications';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Geomembrana PEAD: Aplicações, Soldagem e Equipamentos',
  description:
    'Entenda o que é geomembrana PEAD, suas características, aplicações em aterros, mineração, lagoas e piscicultura, e os equipamentos para soldagem.',
  url: 'https://maquinadesoldageomembrana.com.br/geomembrana-pead',
  inLanguage: 'pt-BR',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'O que é geomembrana PEAD?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A geomembrana PEAD é uma manta impermeável de polietileno de alta densidade, utilizada como barreira de contenção de líquidos e gases em aterros sanitários, reservatórios, mineração e piscicultura.',
      },
    },
    {
      '@type': 'Question',
      name: 'Qual a espessura mais comum da geomembrana PEAD?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'As espessuras mais comuns variam de 0,5 mm a 3,0 mm, conforme a aplicação. Reservatórios e piscicultura usam mantas mais finas, enquanto aterros e mineração exigem espessuras maiores.',
      },
    },
    {
      '@type': 'Question',
      name: 'Qual equipamento é usado para soldar geomembrana PEAD?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A solda por cunha quente é a mais utilizada para grandes áreas planas, criando solda dupla com canal de teste. A extrusora é usada para cantos, junções e reparos.',
      },
    },
  ],
};

const faq = [
  {
    question: 'O que é geomembrana PEAD?',
    answer:
      'A geomembrana PEAD é uma manta impermeável de polietileno de alta densidade, utilizada como barreira de contenção de líquidos e gases em aterros sanitários, reservatórios, mineração e piscicultura.',
  },
  {
    question: 'Qual a espessura mais comum da geomembrana PEAD?',
    answer:
      'As espessuras mais comuns variam de 0,5 mm a 3,0 mm, conforme a aplicação. Reservatórios e piscicultura usam mantas mais finas, enquanto aterros e mineração exigem espessuras maiores.',
  },
  {
    question: 'Qual equipamento é usado para soldar geomembrana PEAD?',
    answer:
      'A solda por cunha quente é a mais utilizada para grandes áreas planas, criando solda dupla com canal de teste. A extrusora é usada para cantos, junções e reparos.',
  },
];

export default function GeomembranaPeadPage() {
  const compativeis = products.filter((p) => p.compatibilidade.pead);

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <PageBreadcrumb items={[{ label: 'Geomembrana PEAD' }]} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          <div className="lg:col-span-2">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 uppercase leading-tight mb-6">
              Geomembrana PEAD: Aplicações, Soldagem e Equipamentos
            </h1>

            <div className="space-y-5 text-foreground-600 leading-relaxed">
              <p>
                A <strong className="text-foreground-950">geomembrana PEAD</strong> (polietileno de alta densidade) é uma manta sintética impermeável amplamente utilizada como barreira de contenção em obras de engenharia e projetos ambientais. Sua alta resistência química e mecânica a torna o material de referência para impermeabilização profissional.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Características do PEAD</h2>
              <ul className="space-y-2">
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Alta resistência química</strong> — suporta ácidos, bases e agentes agressivos.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Baixa permeabilidade</strong> — barreira eficaz contra líquidos e gases.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Resistência UV e intempéries</strong> — adequado para aplicações expostas e enterradas.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Durabilidade</strong> — longa vida útil com baixa necessidade de manutenção.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Flexibilidade</strong> — acompanha o terreno sem perder a função de barreira.</span></li>
              </ul>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Aplicações</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
                {applications.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 bg-background-100 rounded-xl border border-background-200/70 p-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                      <i className="ri-building-2-line text-accent-600 text-sm" />
                    </div>
                    <span className="text-foreground-800 text-sm font-medium">{a.title}</span>
                  </div>
                ))}
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Soldagem de Geomembrana PEAD</h2>
              <p>
                A união das mantas de PEAD é feita por <strong>termofusão</strong>, método que funde as bordas das folhas sobrepostas por calor controlado. Os dois processos principais são:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-5">
                <div className="bg-background-100 rounded-xl border border-background-200/70 p-5">
                  <h3 className="font-heading text-sm font-bold text-foreground-900 uppercase mb-2 flex items-center gap-2">
                    <i className="ri-fire-line text-primary-500" /> Cunha Quente
                  </h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">Funde as duas folhas simultaneamente, criando solda dupla com canal de teste. Ideal para grandes áreas planas.</p>
                </div>
                <div className="bg-background-100 rounded-xl border border-background-200/70 p-5">
                  <h3 className="font-heading text-sm font-bold text-foreground-900 uppercase mb-2 flex items-center gap-2">
                    <i className="ri-drop-line text-accent-500" /> Extrusão
                  </h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">Aplica fio de PEAD fundido sobre a emenda. Ideal para cantos, junções e reparos.</p>
                </div>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Equipamentos Compatíveis com PEAD</h2>
              <p>Os seguintes modelos trabalham com geomembrana PEAD:</p>
              <div className="flex flex-wrap gap-2 my-4">
                {compativeis.map((p) => (
                  <span key={p.id} className="inline-flex items-center gap-1 text-xs font-medium bg-background-100 text-foreground-700 px-3 py-1.5 rounded-full">
                    {p.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mb-5">Perguntas Frequentes</h2>
              <FaqAccordion items={faq} />
            </div>

            <RelatedLinks
              links={[
                { to: '/maquina-de-solda-pead', label: 'Máquina de Solda PEAD' },
                { to: '/soldagem-de-geomembrana', label: 'Técnicas de Soldagem' },
                { to: '/maquina-de-solda-de-geomembrana', label: 'Máquina de Solda de Geomembrana' },
                { to: '/modelos-de-maquinas', label: 'Comparativo de Modelos' },
              ]}
            />
          </div>

          <div className="space-y-6">{ctaSidebar}</div>
        </div>
      </div>
    </Layout>
  );
}